/**
 * Fake Camera 設置模塊
 * 為每個 Fragments 模型創建超大視錐體的假相機,防止元素消失
 * 
 * 根據 FAKE_CAMERA_SETUP.md 的設計
 */

import * as THREE from 'three';

/**
 * 創建 Fake Camera
 * @param modelId 模型 ID
 * @returns OrthographicCamera 實例
 */
export function createFakeCameraForModel(modelId: string): THREE.OrthographicCamera {
  // 強制副作用,防止被 tree-shaking
  const timestamp = Date.now();
  
  // 創建正交相機,視錐體覆蓋 -100k 到 +100k
  const fakeCamera = new THREE.OrthographicCamera(
    -100000,  // left   - X 軸左邊界
    100000,   // right  - X 軸右邊界
    100000,   // top    - Y 軸上邊界
    -100000,  // bottom - Y 軸下邊界
    0.001,    // near   - 近裁剪面
    1000000   // far    - 遠裁剪面 (極遠)
  );
  
  // 位置在模型上方,向下俯視
  fakeCamera.position.set(0, 50000, 0);
  fakeCamera.lookAt(0, 0, 0);
  fakeCamera.updateProjectionMatrix();
  
  // 觸發自定義事件
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('fake-camera-created', { 
      detail: { modelId, timestamp: Date.now(), frustum: { left: -100000, right: 100000 } } 
    }));
  }
  
  return fakeCamera;
}

/**
 * 應用 Fake Camera 到模型
 * @param model Fragments 模型
 * @param modelId 模型 ID
 */
export function applyFakeCameraToModel(model: any, modelId: string): THREE.OrthographicCamera {
  const fakeCamera = createFakeCameraForModel(modelId);
  
  // 將假相機綁定到模型
  model.useCamera(fakeCamera);
  
  // 觸發自定義事件
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('model-using-fake-camera', { 
      detail: { modelId, timestamp: Date.now() } 
    }));
  }
  
  return fakeCamera;
}

/**
 * 為 MultiModelManager 的所有模型設置 fake camera
 * @param modelManager MultiModelManager 實例
 */
export function setupFakeCamerasForAllModels(modelManager: any): void {
  const models = modelManager.getAllModels();
  let count = 0;
  
  models.forEach((modelInfo: any, modelId: string) => {
    // GLTF 不需要 fake camera
    if (modelInfo.modelType === 'gltf') {
      return;
    }
    
    try {
      const fakeCamera = applyFakeCameraToModel(modelInfo.model, modelId);
      count++;
    } catch (error) {
      console.error(`❌ Failed to apply fake camera to ${modelId}:`, error);
    }
  });
}

// 強制導出到全局,防止被 tree-shaking
if (typeof window !== 'undefined') {
  (window as any).__fakeCameraSetup = {
    createFakeCameraForModel,
    applyFakeCameraToModel,
    setupFakeCamerasForAllModels
  };
}
